import { useAbility } from '@casl/react';
import dayjs from 'dayjs';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { ACTIONS } from 'src/auth/utils/defineAbilityFor';
import { DateRangeType } from 'src/constants';
import { AbilityContext } from 'src/context/canContext';
import { layoutOptions } from 'src/features/dashboard/models/layoutPreviews';
import {
  LayoutTemplate,
  Widget,
  getAvailableLayoutOptions,
  getDashboardLayoutByTemplate,
  getValidatedUserTemplate,
  widgetSubject,
} from 'src/features/dashboard/models/layouts';
import {
  buildDashboardControlParams,
  parseDashboardControlParams,
  resolveDashboardZoneIdForUrl,
} from 'src/features/dashboard/utils/dashboardUrlState';
import { useAppSelector } from 'src/hooks/hooks';
import { useApplicationGroups } from 'src/hooks/useApplicationGroups';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import { useUserLocalStorage } from 'src/hooks/useStorage';
import {
  hasConcreteSelection,
  isAllSelection,
  isAllUtilitySelection,
} from 'src/utils/selection';
import { userStorage } from 'src/utils/userStorage';
import {
  LayoutGrid,
  LayoutOption,
} from 'switch-fe-shared/components/Layout/types';
import { withoutItems } from 'switch-fe-shared/components/Layout/withoutItems';
import { ROLES } from 'switch-shared/modules/crud/ability-role/roles.enums';
import {
  UtilityType,
  UtilityTypeOrAll,
} from 'switch-shared/modules/crud/utility/utility.enums';
import { Zone } from 'switch-shared/modules/crud/zone/zone.entity';
import { DurationUnit } from 'switch-shared/utils/duration.enum';

export interface DateRange {
  start: Date;
  end: Date;
}

export interface BucketOptions {
  unit: DurationUnit;
  size: number;
}

export interface DashboardContextValue {
  dateRange: DateRange;
  setDateRange: (newRange: DateRange) => void;
  bucketOptions: BucketOptions;
  setBucketOptions: (newOptions: BucketOptions) => void;
  dateRangeKey: DateRangeType;
  setDateRangeKey: (newKey: DateRangeType) => void;
  utility: UtilityTypeOrAll;
  setUtility: (newUtility: UtilityTypeOrAll) => void;
  zone: Zone | undefined;
  setZone: (newZone: Zone | undefined) => void;
  zoneCount: number;
  setZoneCount: (newCount: number) => void;
  currentTemplate: LayoutTemplate;
  availableLayoutOptions: LayoutOption<LayoutTemplate>[];
  dashboard: LayoutGrid<Widget>;
  handleLayoutChange: (newLayout: LayoutTemplate) => void;
  isAllGroupsSelected: boolean;

  /** Utilities visible in the selected group, excluding fixed fees. */
  availableUtilities: UtilityType[];
}

interface DashboardProviderProps {
  children: React.ReactNode;
}

export const DashboardContext = createContext<DashboardContextValue>(
  {} as DashboardContextValue
);

export const DashboardProvider = ({ children }: DashboardProviderProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardUrlState = useMemo(
    () => parseDashboardControlParams(searchParams),
    [searchParams]
  );
  const [dateRange, setDateRange] = useState({
    start: dayjs().startOf('week').toDate(),
    end: dayjs().endOf('week').toDate(),
  });

  const [bucketOptions, setBucketOptions] = useState({
    unit: DurationUnit.Days,
    size: 1,
  });

  const [dateRangeKey, setDateRangeKey] = useState<DateRangeType>(
    DateRangeType.WEEK
  );

  const [storedUtility, setStoredUtility] =
    useUserLocalStorage<UtilityTypeOrAll>(
      `dashboard/utility`,
      UtilityType.ELECTRICITY
    );
  const utility = dashboardUrlState.utility ?? storedUtility;

  const [zone, setZone] = useUserLocalStorage<Zone | undefined>(
    `dashboard/zone`,
    undefined
  );

  const setUtility = useCallback(
    (newUtility: UtilityTypeOrAll) => {
      setStoredUtility(newUtility);
      const nextParams = buildDashboardControlParams(searchParams, {
        utility: newUtility,
        zoneId: resolveDashboardZoneIdForUrl(
          dashboardUrlState.zoneId,
          zone?._id
        ),
      });

      if (nextParams.toString() !== searchParams.toString()) {
        setSearchParams(nextParams, { replace: true });
      }
    },
    [
      dashboardUrlState.zoneId,
      searchParams,
      setSearchParams,
      setStoredUtility,
      zone?._id,
    ]
  );

  const [zoneCount, setZoneCount] = useState(0);

  const appId = useEffectiveApplicationId();
  const appGroupId = useEffectiveApplicationGroupId();
  const { applicationGroups } = useApplicationGroups();
  const { abilityRoles } = useAppSelector((state) => state.abilityRole);
  const ability = useAbility(AbilityContext);
  const isSuperAdmin = useMemo(
    () => abilityRoles.some((role) => role.role === ROLES.SUPER_ADMIN),
    [abilityRoles]
  );
  const availableUtilities = useMemo(() => {
    const appGroup = applicationGroups.find(
      (group) => group._id === appGroupId
    );
    const utilityTypes = appGroup?.utilityTypes ?? Object.values(UtilityType);

    return utilityTypes.filter(
      (utilityType) => utilityType !== UtilityType.FIXED_FEE
    );
  }, [applicationGroups, appGroupId]);

  const canUseAllGroups = useMemo(
    () =>
      applicationGroups.filter(
        (group) => !isAllSelection(group._id) && group.name !== 'All Groups'
      ).length > 1,
    [applicationGroups]
  );

  // Check if "All Groups" is selected and available for the current user.
  const isAllGroupsSelected = isAllSelection(appGroupId) && canUseAllGroups;

  // Validate saved template - if user doesn't have access, reset to default
  const validatedTemplate = useMemo(() => {
    return getValidatedUserTemplate(abilityRoles);
  }, [abilityRoles]);

  const [userSelectedTemplate, setUserSelectedTemplate] =
    useState<LayoutTemplate>(validatedTemplate);

  // Determine the active template - auto-switch to groups-overview when "All Groups" is selected
  const currentTemplate: LayoutTemplate = useMemo(() => {
    if (isAllGroupsSelected) return 'groups-overview';
    return userSelectedTemplate;
  }, [isAllGroupsSelected, userSelectedTemplate]);

  // Filter layout options based on user role and current selection
  const availableLayoutOptions = useMemo(() => {
    return getAvailableLayoutOptions(
      abilityRoles,
      isAllGroupsSelected,
      layoutOptions
    );
  }, [abilityRoles, isAllGroupsSelected]);

  // Memoize dashboard to create fresh widget instances when template changes
  const dashboard = useMemo(
    () =>
      withoutItems(
        getDashboardLayoutByTemplate(
          currentTemplate,
          isAllSelection(appId),
          isAllUtilitySelection(utility),
          isSuperAdmin,
          availableUtilities
        ),
        (widget) => {
          const subject = widgetSubject(widget, hasConcreteSelection(appId));
          return subject !== undefined && ability.cannot(ACTIONS.READ, subject);
        }
      ),
    [currentTemplate, appId, utility, isSuperAdmin, availableUtilities, ability]
  );

  function handleLayoutChange(template: LayoutTemplate) {
    // Don't allow changing layout when "All Groups" is selected (locked to groups-overview)
    if (isAllGroupsSelected) {
      return;
    }
    setUserSelectedTemplate(template);
    userStorage.setItem('dashboard-template', template);
  }

  // Reset to user's saved template when switching away from "All Groups"
  useEffect(() => {
    if (!isAllGroupsSelected && userSelectedTemplate !== validatedTemplate) {
      // Keep the user's last selected template when they switch back
    }
  }, [isAllGroupsSelected, userSelectedTemplate, validatedTemplate]);

  return (
    <DashboardContext.Provider
      value={{
        dateRange,
        setDateRange,
        bucketOptions,
        setBucketOptions,
        dateRangeKey,
        setDateRangeKey,
        utility,
        setUtility,
        zone,
        setZone,
        zoneCount,
        setZoneCount,
        currentTemplate,
        availableLayoutOptions,
        dashboard,
        handleLayoutChange,
        isAllGroupsSelected,
        availableUtilities,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  return useContext(DashboardContext);
};
